import React, { useState, useMemo } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "./ui/card";
import { Button } from "./ui/button";
import { 
  ArrowLeft,
  Calculator,
  DollarSign,
  Home,
  Zap,
  Phone,
  Car,
  Baby,
  ShoppingCart,
  Download,
  CheckCircle,
  AlertTriangle,
  XCircle,
  Info,
  RefreshCw
} from "lucide-react";
import { Link } from "react-router-dom";
import Header from "./Header";
import Footer from "./Footer";

const BudgetCalculator = () => {
  // Income state
  const [income, setIncome] = useState({
    employment: "",
    benefits: "",
    other: ""
  });

  // Fixed expenses state
  const [fixedExpenses, setFixedExpenses] = useState({
    electric: "",
    gas: "",
    water: "",
    phone: "",
    transportation: "",
    childcare: "",
    otherFixed: ""
  });

  // Living expenses state
  const [livingExpenses, setLivingExpenses] = useState({
    groceries: "",
    household: "",
    personalCare: "",
    laundry: "",
    otherLiving: ""
  });

  // Helper to parse number or return 0
  const parseNum = (val) => parseFloat(val) || 0;

  // Calculate totals
  const calculations = useMemo(() => {
    const totalIncome = parseNum(income.employment) + parseNum(income.benefits) + parseNum(income.other);
    
    const totalFixed = parseNum(fixedExpenses.electric) + parseNum(fixedExpenses.gas) + 
      parseNum(fixedExpenses.water) + parseNum(fixedExpenses.phone) + 
      parseNum(fixedExpenses.transportation) + parseNum(fixedExpenses.childcare) + 
      parseNum(fixedExpenses.otherFixed);
    
    const totalLiving = parseNum(livingExpenses.groceries) + parseNum(livingExpenses.household) + 
      parseNum(livingExpenses.personalCare) + parseNum(livingExpenses.laundry) + 
      parseNum(livingExpenses.otherLiving);
    
    const totalExpenses = totalFixed + totalLiving;
    const availableForRent = totalIncome - totalExpenses;
    const recommendedRent = totalIncome * 0.30;
    const maxRent = totalIncome * 0.35;

    return {
      totalIncome,
      totalFixed,
      totalLiving,
      totalExpenses,
      availableForRent,
      recommendedRent,
      maxRent
    };
  }, [income, fixedExpenses, livingExpenses]);

  // Determine affordability status
  const getAffordabilityStatus = () => {
    if (calculations.totalIncome === 0) return null;
    
    if (calculations.availableForRent >= calculations.recommendedRent) {
      return {
        status: "good",
        icon: <CheckCircle className="w-8 h-8 text-green-600" />,
        color: "bg-green-50 border-green-200",
        textColor: "text-green-800",
        message: "Great! You have a healthy budget for rent."
      };
    } else if (calculations.availableForRent >= calculations.maxRent * 0.5) {
      return {
        status: "caution",
        icon: <AlertTriangle className="w-8 h-8 text-yellow-600" />,
        color: "bg-yellow-50 border-yellow-200",
        textColor: "text-yellow-800",
        message: "Caution: Your budget is tight. Look for ways to reduce expenses."
      };
    } else {
      return {
        status: "warning",
        icon: <XCircle className="w-8 h-8 text-red-600" />,
        color: "bg-red-50 border-red-200",
        textColor: "text-red-800",
        message: "Your expenses are high. Consider reducing costs or increasing income."
      };
    }
  };

  const affordabilityStatus = getAffordabilityStatus();

  // Reset calculator
  const handleReset = () => {
    setIncome({ employment: "", benefits: "", other: "" });
    setFixedExpenses({ electric: "", gas: "", water: "", phone: "", transportation: "", childcare: "", otherFixed: "" });
    setLivingExpenses({ groceries: "", household: "", personalCare: "", laundry: "", otherLiving: "" });
  };

  // Input component for accessibility
  const MoneyInput = ({ label, icon, value, onChange, placeholder = "0" }) => (
    <div className="flex items-center space-x-3 p-3 bg-white rounded-lg border border-gray-200 hover:border-[#87A96B] transition-colors">
      <div className="w-10 h-10 rounded-full bg-gray-100 flex items-center justify-center flex-shrink-0">
        {icon}
      </div>
      <div className="flex-1">
        <label className="block text-sm font-medium text-gray-700 mb-1">{label}</label>
        <div className="relative">
          <span className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-500 font-semibold">$</span>
          <input
            type="number"
            value={value}
            onChange={(e) => onChange(e.target.value)}
            placeholder={placeholder}
            className="w-full pl-8 pr-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#3F5D4A] focus:border-[#3F5D4A] text-lg"
            min="0"
            step="1"
            aria-label={label}
          />
        </div>
      </div>
    </div>
  );

  return (
    <div className="min-h-screen bg-[#F8F4ED]">
      <Header />
      <div className="py-20 px-4">
        <div className="container mx-auto max-w-5xl">
          
          {/* Back Navigation */}
          <div className="mb-8">
            <Link to="/resources/renters" className="inline-flex items-center text-gray-600 hover:text-[#3F5D4A] transition-colors duration-200">
              <ArrowLeft className="w-4 h-4 mr-2" />
              Back to Renter Resources
            </Link>
          </div>

          {/* Page Title */}
          <div className="text-center space-y-6 mb-12">
            <div 
              className="inline-flex items-center px-4 py-2 rounded-full text-white text-sm font-semibold"
              style={{backgroundColor: '#3F5D4A'}}
            >
              <Calculator className="w-4 h-4 mr-2" />
              INTERACTIVE TOOL
            </div>
            <h1 className="text-4xl md:text-5xl font-bold text-gray-800">
              Rental Budget <span style={{color: '#3F5D4A'}}>Calculator</span>
            </h1>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto leading-relaxed">
              Find out how much rent you can afford based on your real income and expenses. 
            Enter your numbers below — we'll do the math for you!
          </p>
        </div>

        {/* How It Works */}
        <div className="bg-[#F0EBE0] border border-[#D4C9B0] rounded-xl p-6 mb-8">
          <div className="flex items-start space-x-3">
            <Info className="w-6 h-6 text-[#3F5D4A] flex-shrink-0 mt-1" />
            <div>
              <h3 className="font-bold text-[#3F5D4A] text-lg mb-2">How This Works</h3>
              <ol className="text-[#5C5852] space-y-1 list-decimal list-inside">
                <li>Enter your monthly income (all money coming in)</li>
                <li>Enter your monthly bills and expenses (money going out)</li>
                <li>See how much you can afford for rent!</li>
              </ol>
            </div>
          </div>
        </div>

        <div className="grid lg:grid-cols-2 gap-8">
          
          {/* Left Column - Inputs */}
          <div className="space-y-6">
            
            {/* Income Section */}
            <Card className="border-0 shadow-lg">
              <CardHeader className="pb-4" style={{backgroundColor: '#87A96B', borderRadius: '8px 8px 0 0'}}>
                <CardTitle className="text-xl font-bold text-white flex items-center">
                  <DollarSign className="w-6 h-6 mr-2" />
                  Step 1: Your Monthly Income
                </CardTitle>
                <p className="text-white text-sm opacity-90">Money you receive each month</p>
              </CardHeader>
              <CardContent className="space-y-4 pt-6">
                <MoneyInput
                  label="Job / Wages"
                  icon={<DollarSign className="w-5 h-5 text-gray-600" />}
                  value={income.employment}
                  onChange={(val) => setIncome({...income, employment: val})}
                />
                <MoneyInput
                  label="Benefits / Assistance (SSI, SNAP, etc.)"
                  icon={<DollarSign className="w-5 h-5 text-gray-600" />}
                  value={income.benefits}
                  onChange={(val) => setIncome({...income, benefits: val})}
                />
                <MoneyInput
                  label="Other Income"
                  icon={<DollarSign className="w-5 h-5 text-gray-600" />}
                  value={income.other}
                  onChange={(val) => setIncome({...income, other: val})}
                />
                <div className="p-4 bg-green-50 rounded-lg border border-green-200">
                  <div className="flex justify-between items-center">
                    <span className="font-semibold text-green-800">Total Monthly Income:</span>
                    <span className="text-2xl font-bold text-green-700">${calculations.totalIncome.toLocaleString()}</span>
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* Fixed Expenses Section */}
            <Card className="border-0 shadow-lg">
              <CardHeader className="pb-4" style={{backgroundColor: '#3F5D4A', borderRadius: '8px 8px 0 0'}}>
                <CardTitle className="text-xl font-bold text-white flex items-center">
                  <Home className="w-6 h-6 mr-2" />
                  Step 2: Monthly Bills
                </CardTitle>
                <p className="text-white text-sm opacity-90">Bills you pay every month (not including rent)</p>
              </CardHeader>
              <CardContent className="space-y-4 pt-6">
                <MoneyInput
                  label="Electric Bill"
                  icon={<Zap className="w-5 h-5 text-yellow-600" />}
                  value={fixedExpenses.electric}
                  onChange={(val) => setFixedExpenses({...fixedExpenses, electric: val})}
                />
                <MoneyInput
                  label="Gas Bill"
                  icon={<Zap className="w-5 h-5 text-orange-600" />}
                  value={fixedExpenses.gas}
                  onChange={(val) => setFixedExpenses({...fixedExpenses, gas: val})}
                />
                <MoneyInput
                  label="Water Bill"
                  icon={<Home className="w-5 h-5 text-[#3F5D4A]" />}
                  value={fixedExpenses.water}
                  onChange={(val) => setFixedExpenses({...fixedExpenses, water: val})}
                />
                <MoneyInput
                  label="Phone Bill"
                  icon={<Phone className="w-5 h-5 text-purple-600" />}
                  value={fixedExpenses.phone}
                  onChange={(val) => setFixedExpenses({...fixedExpenses, phone: val})}
                />
                <MoneyInput
                  label="Transportation (Car, Bus Pass, Gas)"
                  icon={<Car className="w-5 h-5 text-gray-600" />}
                  value={fixedExpenses.transportation}
                  onChange={(val) => setFixedExpenses({...fixedExpenses, transportation: val})}
                />
                <MoneyInput
                  label="Childcare"
                  icon={<Baby className="w-5 h-5 text-pink-600" />}
                  value={fixedExpenses.childcare}
                  onChange={(val) => setFixedExpenses({...fixedExpenses, childcare: val})}
                />
                <MoneyInput
                  label="Other Bills (Insurance, Subscriptions)"
                  icon={<DollarSign className="w-5 h-5 text-gray-600" />}
                  value={fixedExpenses.otherFixed}
                  onChange={(val) => setFixedExpenses({...fixedExpenses, otherFixed: val})}
                />
                <div className="p-4 bg-[#F0EBE0] rounded-lg border border-[#D4C9B0]">
                  <div className="flex justify-between items-center">
                    <span className="font-semibold text-[#3F5D4A]">Total Monthly Bills:</span>
                    <span className="text-2xl font-bold text-[#3F5D4A]">${calculations.totalFixed.toLocaleString()}</span>
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* Living Expenses Section */}
            <Card className="border-0 shadow-lg">
              <CardHeader className="pb-4" style={{backgroundColor: '#87A96B', borderRadius: '8px 8px 0 0'}}>
                <CardTitle className="text-xl font-bold text-white flex items-center">
                  <ShoppingCart className="w-6 h-6 mr-2" />
                  Step 3: Everyday Spending
                </CardTitle>
                <p className="text-white text-sm opacity-90">Things you buy regularly</p>
              </CardHeader>
              <CardContent className="space-y-4 pt-6">
                <MoneyInput
                  label="Groceries / Food"
                  icon={<ShoppingCart className="w-5 h-5 text-green-600" />}
                  value={livingExpenses.groceries}
                  onChange={(val) => setLivingExpenses({...livingExpenses, groceries: val})}
                />
                <MoneyInput
                  label="Household Supplies (Cleaning, etc.)"
                  icon={<Home className="w-5 h-5 text-teal-600" />}
                  value={livingExpenses.household}
                  onChange={(val) => setLivingExpenses({...livingExpenses, household: val})}
                />
                <MoneyInput
                  label="Personal Care (Haircuts, Toiletries)"
                  icon={<DollarSign className="w-5 h-5 text-pink-600" />}
                  value={livingExpenses.personalCare}
                  onChange={(val) => setLivingExpenses({...livingExpenses, personalCare: val})}
                />
                <MoneyInput
                  label="Laundry"
                  icon={<DollarSign className="w-5 h-5 text-[#3F5D4A]" />}
                  value={livingExpenses.laundry}
                  onChange={(val) => setLivingExpenses({...livingExpenses, laundry: val})}
                />
                <MoneyInput
                  label="Other Spending"
                  icon={<DollarSign className="w-5 h-5 text-gray-600" />}
                  value={livingExpenses.otherLiving}
                  onChange={(val) => setLivingExpenses({...livingExpenses, otherLiving: val})}
                />
                <div className="p-4 bg-green-50 rounded-lg border border-green-200">
                  <div className="flex justify-between items-center">
                    <span className="font-semibold text-green-800">Total Everyday Spending:</span>
                    <span className="text-2xl font-bold text-green-700">${calculations.totalLiving.toLocaleString()}</span>
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>

          {/* Right Column - Results */}
          <div className="space-y-6">
            
            {/* Results Card - Sticky */}
            <div className="lg:sticky lg:top-8 space-y-6">
              
              <Card className="border-0 shadow-xl">
                <CardHeader className="pb-4 text-center" style={{backgroundColor: '#3F5D4A', borderRadius: '8px 8px 0 0'}}>
                  <CardTitle className="text-2xl font-bold text-white">
                    Your Results
                  </CardTitle>
                </CardHeader>
                <CardContent className="pt-6 space-y-6">
                  
                  {/* Summary */}
                  <div className="space-y-4">
                    <div className="flex justify-between items-center p-3 bg-gray-50 rounded-lg">
                      <span className="text-gray-600">Total Income:</span>
                      <span className="font-bold text-lg text-green-600">+${calculations.totalIncome.toLocaleString()}</span>
                    </div>
                    <div className="flex justify-between items-center p-3 bg-gray-50 rounded-lg">
                      <span className="text-gray-600">Total Expenses:</span>
                      <span className="font-bold text-lg text-red-600">-${calculations.totalExpenses.toLocaleString()}</span>
                    </div>
                    <hr className="border-gray-300" />
                    <div className="flex justify-between items-center p-4 bg-[#F0EBE0] rounded-lg border-2 border-[#D4C9B0]">
                      <span className="font-semibold text-[#3F5D4A] text-lg">Money Left for Rent:</span>
                      <span className="font-bold text-2xl" style={{color: '#3F5D4A'}}>${calculations.availableForRent.toLocaleString()}</span>
                    </div>
                  </div>

                  {/* Recommended Rent */}
                  <div className="p-4 rounded-lg" style={{backgroundColor: '#87A96B20', border: '2px solid #87A96B'}}>
                    <h4 className="font-bold text-lg mb-2" style={{color: '#87A96B'}}>Recommended Max Rent</h4>
                    <p className="text-gray-600 text-sm mb-2">Based on the 30% rule (rent should be 30% or less of income)</p>
                    <div className="text-3xl font-bold" style={{color: '#87A96B'}}>
                      ${calculations.recommendedRent.toLocaleString(undefined, {maximumFractionDigits: 0})} / month
                    </div>
                  </div>

                  {/* Status Indicator */}
                  {affordabilityStatus && (
                    <div className={`p-4 rounded-lg border-2 ${affordabilityStatus.color}`}>
                      <div className="flex items-center space-x-3">
                        {affordabilityStatus.icon}
                        <p className={`font-semibold ${affordabilityStatus.textColor}`}>
                          {affordabilityStatus.message}
                        </p>
                      </div>
                    </div>
                  )}

                  {/* Visual Progress Bar */}
                  {calculations.totalIncome > 0 && (
                    <div className="space-y-2">
                      <h4 className="font-semibold text-gray-700">Where Your Money Goes:</h4>
                      <div className="h-8 bg-gray-200 rounded-full overflow-hidden flex">
                        <div 
                          className="h-full flex items-center justify-center text-white text-xs font-semibold"
                          style={{
                            width: `${Math.min((calculations.totalExpenses / calculations.totalIncome) * 100, 100)}%`,
                            backgroundColor: '#DC2626'
                          }}
                        >
                          {calculations.totalExpenses > 0 && 'Expenses'}
                        </div>
                        <div 
                          className="h-full flex items-center justify-center text-white text-xs font-semibold"
                          style={{
                            width: `${Math.max(100 - (calculations.totalExpenses / calculations.totalIncome) * 100, 0)}%`,
                            backgroundColor: '#87A96B'
                          }}
                        >
                          {calculations.availableForRent > 0 && 'For Rent'}
                        </div>
                      </div>
                      <div className="flex justify-between text-sm text-gray-500">
                        <span>Expenses: {((calculations.totalExpenses / calculations.totalIncome) * 100).toFixed(0)}%</span>
                        <span>Available: {((calculations.availableForRent / calculations.totalIncome) * 100).toFixed(0)}%</span>
                      </div>
                    </div>
                  )}

                  {/* Reset Button */}
                  <Button 
                    onClick={handleReset}
                    variant="outline"
                    className="w-full py-3 border-2 border-gray-300 text-gray-700 font-semibold hover:bg-gray-50"
                  >
                    <RefreshCw className="w-4 h-4 mr-2" />
                    Start Over
                  </Button>
                </CardContent>
              </Card>

              {/* Download Worksheet CTA */}
              <Card className="border-0 shadow-lg" style={{backgroundColor: '#3F5D4A'}}>
                <CardContent className="p-6 text-center text-white">
                  <Download className="w-10 h-10 mx-auto mb-4 opacity-90" />
                  <h3 className="text-xl font-bold mb-2">Want to Plan Further?</h3>
                  <p className="text-sm opacity-90 mb-4">
                    Download our Budget Worksheet to track your expenses in detail and create a long-term plan.
                  </p>
                  <Link to="/resources/renters/budget-worksheet">
                    <Button 
                      className="w-full py-3 font-semibold"
                      style={{backgroundColor: '#87A96B'}}
                    >
                      <Download className="w-4 h-4 mr-2" />
                      Open Branded Budget Worksheet
                    </Button>
                  </Link>
                </CardContent>
              </Card>

            </div>
          </div>
        </div>

        {/* Tips Section */}
        <div className="mt-12 bg-white rounded-2xl shadow-lg p-8">
          <h2 className="text-2xl font-bold text-gray-800 mb-6 text-center">Money-Saving Tips</h2>
          <div className="grid md:grid-cols-3 gap-6">
            <div className="text-center p-4">
              <div className="w-12 h-12 rounded-full mx-auto mb-3 flex items-center justify-center" style={{backgroundColor: '#87A96B20'}}>
                <Zap className="w-6 h-6" style={{color: '#87A96B'}} />
              </div>
              <h4 className="font-semibold text-gray-800 mb-2">Lower Utility Bills</h4>
              <p className="text-gray-600 text-sm">Turn off lights, unplug devices, and use energy-efficient bulbs to save money.</p>
            </div>
            <div className="text-center p-4">
              <div className="w-12 h-12 rounded-full mx-auto mb-3 flex items-center justify-center" style={{backgroundColor: '#3F5D4A20'}}>
                <ShoppingCart className="w-6 h-6" style={{color: '#3F5D4A'}} />
              </div>
              <h4 className="font-semibold text-gray-800 mb-2">Smart Grocery Shopping</h4>
              <p className="text-gray-600 text-sm">Make a list, use coupons, and buy store brands to stretch your food budget.</p>
            </div>
            <div className="text-center p-4">
              <div className="w-12 h-12 rounded-full mx-auto mb-3 flex items-center justify-center" style={{backgroundColor: '#87A96B20'}}>
                <DollarSign className="w-6 h-6" style={{color: '#87A96B'}} />
              </div>
              <h4 className="font-semibold text-gray-800 mb-2">Track Every Dollar</h4>
              <p className="text-gray-600 text-sm">Write down everything you spend for one month to find places to cut back.</p>
            </div>
          </div>
        </div>

        {/* Disclaimer */}
        <div className="mt-8 bg-[#F0EBE0] border border-[#D4C9B0] rounded-lg p-6">
          <h3 className="font-semibold text-[#3F5D4A] mb-2">Important Note</h3>
          <p className="text-[#5C5852] text-sm">
            This calculator provides estimates to help you plan. Actual costs may vary. 
            For personalized financial advice, consider speaking with a housing counselor or financial advisor.
            Need help? Contact Dorsey Taylor Homes for guidance.
          </p>
        </div>

        </div>
      </div>
      <Footer />
    </div>
  );
};

export default BudgetCalculator;
